import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { http, errorMessage } from '../lib/api';
import { Spinner } from '../components/Spinner';
import { ArrowLeft, RotateCw } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export function ForgotPasswordPage() {
  useDocumentTitle('Reset password');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await http.post('/auth/forgot-password', { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      setError(errorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="mb-8 flex flex-col items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-ink text-white">
            <RotateCw className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="text-center">
            <h1 className="text-lg font-semibold tracking-tight text-ink">
              aiai·loop
            </h1>
            <p className="mt-1 text-sm text-ink-muted">Reset your password</p>
          </div>
        </div>

        {submitted ? (
          <div className="card p-6 text-center space-y-4">
            <p className="text-sm text-ink">
              If an account exists for <strong>{email}</strong>, you'll receive a
              password reset link shortly. Check your inbox (and spam folder).
            </p>
            <Link to="/login" className="btn-ghost w-full inline-flex justify-center text-sm">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="card space-y-4 p-6">
            <div>
              <label className="label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>

            {error && (
              <div className="rounded-[5px] border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">
                {error}
              </div>
            )}

            <button type="submit" className="btn-primary w-full" disabled={submitting}>
              {submitting && <Spinner />}
              {submitting ? 'Sending…' : 'Send reset link'}
            </button>
            <Link
              to="/login"
              className="btn-ghost w-full inline-flex justify-center text-xs"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
